import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiProgress from "components/VuiProgress";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import LineChart from "examples/Charts/LineCharts/LineChart";

import { getSummary } from "services/api";

// Category colors for donut
const CAT_COLORS = [
  "#0075FF", "#2CD9FF", "#01B574", "#FFB547", "#E31A1A",
  "#7928CA", "#FF0080", "#00D4FF", "#FF6B6B", "#4ECDC4",
  "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD", "#98D8C8",
];

function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    getSummary().then(setData);
  }, []);

  if (!data) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3} display="flex" justifyContent="center" minHeight="60vh">
          <VuiTypography color="white" variant="h5">Loading...</VuiTypography>
        </VuiBox>
      </DashboardLayout>
    );
  }

  const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
  const catTotals = data.category_totals || {};
  const catPcts = data.category_pcts || {};
  const catNames = Object.keys(catTotals).sort((a, b) => catTotals[b] - catTotals[a]);
  const catValues = catNames.map((k) => catTotals[k]);
  const totalCat = catValues.reduce((a, b) => a + b, 0) || 1;

  // Monthly trend data
  const monthlyTrend = data.monthly_trend || [];
  const monthLabels = monthlyTrend.map((m) => {
    const parts = m.month.split("-");
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return months[parseInt(parts[1], 10) - 1] || m.month;
  });

  const trendLineData = [
    { name: "Expenses", data: monthlyTrend.map((m) => m.spend) },
    { name: "Income", data: monthlyTrend.map((m) => m.income) },
  ];

  const trendLineOptions = {
    chart: { toolbar: { show: false }, dropShadow: { enabled: true, top: 5, left: 0, blur: 8, color: "#0075FF", opacity: 0.2 } },
    tooltip: { theme: "dark", y: { formatter: (v) => `Rs. ${Number(v).toLocaleString()}` } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    xaxis: {
      categories: monthLabels,
      labels: { style: { colors: "#c8cfca", fontSize: "12px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#c8cfca", fontSize: "10px" },
        formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
      },
    },
    legend: { show: true, labels: { colors: "#c8cfca" }, position: "top" },
    grid: { strokeDashArray: 5, borderColor: "#56577A" },
    fill: {
      type: "gradient",
      gradient: { shade: "dark", type: "vertical", shadeIntensity: 0, opacityFrom: 0.4, opacityTo: 0, stops: [] },
    },
    colors: ["#E31A1A", "#01B574"],
    markers: { size: 4, colors: ["#E31A1A", "#01B574"], strokeWidth: 0 },
  };

  // Spending change
  const spendChange = data.spend_change_pct || 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Spending by Category Donut */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <Card sx={{ height: "100%", p: 3 }}>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
                  Spending by Category
                </VuiTypography>
                <VuiTypography variant="button" color="text" fontWeight="regular" mb="20px">
                  PKR breakdown this period
                </VuiTypography>

                {/* Custom donut visualization */}
                <VuiBox display="flex" justifyContent="center" alignItems="center" my={3}>
                  <VuiBox position="relative" sx={{ width: "220px", height: "220px" }}>
                    <svg viewBox="0 0 42 42" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
                      {catNames.reduce((acc, cat, i) => {
                        const pct = (catTotals[cat] / totalCat) * 100;
                        const offset = acc.offset;
                        acc.elements.push(
                          <circle
                            key={cat}
                            cx="21" cy="21" r="15.91549430918954"
                            fill="transparent"
                            stroke={CAT_COLORS[i % CAT_COLORS.length]}
                            strokeWidth="3.5"
                            strokeDasharray={`${pct} ${100 - pct}`}
                            strokeDashoffset={`${100 - offset}`}
                            strokeLinecap="round"
                          />
                        );
                        acc.offset = offset + pct;
                        return acc;
                      }, { elements: [], offset: 0 }).elements}
                    </svg>
                    <VuiBox
                      position="absolute"
                      top="50%" left="50%"
                      sx={{ transform: "translate(-50%, -50%)", textAlign: "center" }}
                    >
                      <VuiTypography variant="h4" color="white" fontWeight="bold">
                        Rs. {fmt(totalCat)}
                      </VuiTypography>
                      <VuiTypography variant="caption" color="text">
                        Total Spent
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>
                </VuiBox>

                {/* Category list */}
                <VuiBox>
                  {catNames.slice(0, 8).map((cat, i) => (
                    <VuiBox key={cat} display="flex" justifyContent="space-between" alignItems="center" py={0.8}>
                      <VuiBox display="flex" alignItems="center">
                        <VuiBox
                          sx={{
                            width: "12px", height: "12px", borderRadius: "3px",
                            background: CAT_COLORS[i % CAT_COLORS.length], mr: 1.5, flexShrink: 0,
                          }}
                        />
                        <VuiTypography variant="button" color="white" fontWeight="medium">
                          {cat}
                        </VuiTypography>
                      </VuiBox>
                      <VuiBox display="flex" alignItems="center" gap="12px">
                        <VuiTypography variant="button" color="text" fontWeight="regular">
                          {(catPcts[cat] || 0)}%
                        </VuiTypography>
                        <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ minWidth: "80px", textAlign: "right" }}>
                          Rs. {fmt(catTotals[cat])}
                        </VuiTypography>
                      </VuiBox>
                    </VuiBox>
                  ))}
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Card sx={{ height: "100%", p: 3 }}>
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="5px">
                  <VuiTypography variant="lg" color="white" fontWeight="bold">
                    Monthly Trend
                  </VuiTypography>
                  <VuiBox display="flex" alignItems="center" gap="8px">
                    <VuiBox sx={{ width: 10, height: 10, borderRadius: "50%", background: "#E31A1A" }} />
                    <VuiTypography variant="caption" color="text">Expenses</VuiTypography>
                    <VuiBox sx={{ width: 10, height: 10, borderRadius: "50%", background: "#01B574", ml: 1 }} />
                    <VuiTypography variant="caption" color="text">Income</VuiTypography>
                  </VuiBox>
                </VuiBox>
                <VuiTypography variant="button" color="text" fontWeight="regular" mb="20px">
                  Income vs Expenses (PKR)
                </VuiTypography>
                <VuiBox sx={{ height: "350px" }}>
                  {monthlyTrend.length > 0 ? (
                    <LineChart lineChartData={trendLineData} lineChartOptions={trendLineOptions} />
                  ) : (
                    <VuiTypography color="text" variant="button">No data</VuiTypography>
                  )}
                </VuiBox>
              </Card>
            </Grid>
          </Grid>
        </VuiBox>

        {/* Spending Comparison + Top Categories */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
                  Month Comparison
                </VuiTypography>
                <VuiTypography variant="button" color="text" fontWeight="regular" mb="20px">
                  vs Previous Month
                </VuiTypography>
                <VuiBox textAlign="center" py={3}>
                  <VuiTypography variant="h2" color="white" fontWeight="bold">
                    Rs. {fmt(data.month_spend)}
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text" mb="16px" display="block">
                    This Month
                  </VuiTypography>
                  <VuiBox display="flex" justifyContent="center" alignItems="center" gap="6px" mt={2}>
                    <VuiTypography
                      variant="button"
                      color={spendChange > 0 ? "error" : "success"}
                      fontWeight="bold"
                    >
                      {spendChange > 0 ? "+" : ""}{spendChange}%
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text">
                      vs Rs. {fmt(data.prev_month_spend)} last month
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
                <VuiBox mt={2}>
                  <VuiProgress
                    value={Math.min(Math.abs(spendChange), 100)}
                    color={spendChange > 0 ? "error" : "success"}
                    sx={{ background: "#2D2E5F" }}
                  />
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
                  Money In
                </VuiTypography>
                <VuiTypography variant="button" color="text" fontWeight="regular" mb="20px">
                  Income this month
                </VuiTypography>
                <VuiBox textAlign="center" py={3}>
                  <VuiTypography variant="h2" color="success" fontWeight="bold">
                    Rs. {fmt(data.month_income)}
                  </VuiTypography>
                  {data.month_income_usd > 0 && (
                    <VuiTypography variant="button" color="success" fontWeight="medium" display="block" mt={1}>
                      + ${fmt(data.month_income_usd)} USD
                    </VuiTypography>
                  )}
                  {data.month_income_gbp > 0 && (
                    <VuiTypography variant="button" color="success" fontWeight="medium" display="block">
                      + £{fmt(data.month_income_gbp)} GBP
                    </VuiTypography>
                  )}
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
                  Money Out
                </VuiTypography>
                <VuiTypography variant="button" color="text" fontWeight="regular" mb="20px">
                  Expenses this month
                </VuiTypography>
                <VuiBox textAlign="center" py={3}>
                  <VuiTypography variant="h2" color="error" fontWeight="bold">
                    Rs. {fmt(data.month_spend)}
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text" display="block" mt={1}>
                    {data.total_txns} transactions
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text" display="block">
                    Daily avg: Rs. {fmt(data.daily_avg)}
                  </VuiTypography>
                </VuiBox>
              </Card>
            </Grid>
          </Grid>
        </VuiBox>

        {/* Top Spending Categories Detail */}
        <Card sx={{ p: 3 }}>
          <VuiTypography variant="lg" color="white" fontWeight="bold" mb="20px">
            Category Breakdown
          </VuiTypography>
          <Grid container spacing={2}>
            {catNames.map((cat, i) => (
              <Grid item xs={12} sm={6} md={4} key={cat}>
                <VuiBox
                  p={2}
                  sx={{
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "12px",
                    border: `1px solid ${CAT_COLORS[i % CAT_COLORS.length]}33`,
                  }}
                >
                  <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <VuiBox display="flex" alignItems="center">
                      <VuiBox
                        sx={{
                          width: "10px", height: "10px", borderRadius: "3px",
                          background: CAT_COLORS[i % CAT_COLORS.length], mr: 1,
                        }}
                      />
                      <VuiTypography variant="button" color="white" fontWeight="medium">
                        {cat}
                      </VuiTypography>
                    </VuiBox>
                    <VuiTypography variant="caption" color="text">
                      {catPcts[cat] || 0}%
                    </VuiTypography>
                  </VuiBox>
                  <VuiTypography variant="h6" color="white" fontWeight="bold">
                    Rs. {fmt(catTotals[cat])}
                  </VuiTypography>
                  <VuiProgress
                    value={catPcts[cat] || 0}
                    color="info"
                    sx={{ background: "#2D2E5F", mt: 1 }}
                  />
                </VuiBox>
              </Grid>
            ))}
          </Grid>
        </Card>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analytics;
